import React, { useState, useEffect, useCallback } from 'react';
import {
  Radar,
  Search,
  RefreshCw,
  Calendar,
  ExternalLink,
  Bell,
  BellOff,
  Target,
  Clock,
  DollarSign,
  FileText,
  ChevronDown,
  Star,
  StarOff,
  Loader2,
  AlertCircle,
  Download,
  ArrowUpDown
} from 'lucide-react';
import { grantService, PublicGrant, GrantStats } from '../services/grantService';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/Toast';

const PublicGrantMonitoring: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  // 상태 관리
  const [grants, setGrants] = useState<PublicGrant[]>([]);
  const [stats, setStats] = useState<GrantStats>({ total: 0, active: 0, urgent: 0, bookmarked: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 필터 상태
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSort, setSelectedSort] = useState('deadline_asc');

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  // UI 상태
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [expandedGrant, setExpandedGrant] = useState<string | null>(null);

  // 검색 디바운스 (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // 필터 변경 시 페이지 리셋
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, selectedSource, selectedStatus, selectedCategory, selectedSort]);

  const sortOptions = [
    { value: 'deadline_asc', label: '마감일순' },
    { value: 'deadline_desc', label: '마감일 역순' },
    { value: 'crawled_at_desc', label: '최신 등록순' },
    { value: 'title_asc', label: '제목 가나다순' },
  ];

  const sources = [
    { value: 'all', label: '전체 기관' },
    { value: 'NTIS', label: 'NTIS (국가과학기술정보서비스)' },
    { value: 'IRIS', label: 'IRIS (공모예고)' },
    { value: 'NIPA', label: 'NIPA (정보통신산업진흥원)' },
    { value: 'NIA', label: 'NIA (한국지능정보사회진흥원)' },
    { value: 'KOCCA', label: '한국콘텐츠진흥원' },
    { value: 'NRF', label: 'NRF (한국연구재단)' },
    { value: 'KEIT', label: 'KEIT (한국산업기술평가관리원)' },
    { value: 'IITP', label: 'IITP (정보통신기획평가원)' },
    { value: 'KETEP', label: 'KETEP (한국에너지기술평가원)' },
    { value: 'KHIDI', label: 'KHIDI (한국보건산업진흥원)' },
    { value: 'BIZINFO', label: '기업마당' },
    { value: 'KSTARTUP', label: 'K-Startup (창업지원포털)' },
    { value: 'SMTECH', label: '중소기업기술정보진흥원' },
    { value: 'GBTP', label: '경북경제진흥원' },
    { value: 'KONEPS', label: '나라장터' },
  ];

  const categories = [
    { value: 'all', label: '전체 분야' },
    { value: 'AI', label: 'AI/빅데이터' },
    { value: 'SW', label: 'SW/클라우드' },
    { value: 'XR', label: 'XR/메타버스' },
    { value: 'DX', label: '디지털전환' },
    { value: 'Green', label: '그린/ESG' },
    { value: 'Bio', label: '바이오/헬스케어' },
    { value: 'Manufacturing', label: '제조/스마트팩토리' },
    { value: 'RnD', label: 'R&D/연구개발' },
    { value: 'Startup', label: '창업/스타트업' },
  ];

  // 공모사업 목록 조회
  const fetchGrants = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [sortBy, sortOrder] = selectedSort.split('_');

      const response = await grantService.getGrants({
        source: selectedSource,
        status: selectedStatus,
        category: selectedCategory,
        search: debouncedSearch,
        skip: currentPage * pageSize,
        limit: pageSize,
        sort_by: sortBy === 'crawledAt' ? 'crawled_at' : sortBy,
        sort_order: sortOrder,
      });

      setGrants(response.items);
      setTotalCount(response.total);
      if (response.crawled_at) {
        setLastUpdated(new Date(response.crawled_at));
      }
    } catch (err: any) {
      console.error('공모사업 조회 실패:', err);
      setError(err.message || '공모사업을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedSource, selectedStatus, selectedCategory, debouncedSearch, selectedSort, currentPage]);

  // 통계 조회
  const fetchStats = useCallback(async () => {
    try {
      const statsData = await grantService.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('통계 조회 실패:', err);
    }
  }, []);

  // 초기 로드
  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        await Promise.all([fetchGrants(), fetchStats()]);
      } catch (err: any) {
        if (err.name === 'AbortError') return; // Cleanup, ignore
      }
    };

    load();
    return () => controller.abort();
  }, [fetchGrants, fetchStats]);

  // 새로고침 (크롤링 트리거)
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // 관리자만 크롤링 가능, 일반 사용자는 캐시 새로고침
      await grantService.refreshCache();
      await fetchGrants();
      await fetchStats();
      setLastUpdated(new Date());
    } catch (err: any) {
      console.error('새로고침 실패:', err);
      // 권한 없으면 그냥 목록만 다시 조회
      await fetchGrants();
    } finally {
      setIsRefreshing(false);
    }
  };

  // 북마크 토글
  const toggleBookmark = async (grantId: string) => {
    try {
      const result = await grantService.toggleBookmark(grantId);
      setGrants(prevGrants => 
        prevGrants.map(g => 
          g.id === grantId ? { ...g, is_bookmarked: result.is_bookmarked } : g
        )
      );
      // 통계 갱신
      fetchStats();
    } catch (err: any) {
      console.error('북마크 토글 실패:', err);
      if (err.message?.includes('401') || err.message?.includes('403')) {
        toast.warning('로그인이 필요한 기능입니다.');
      } else {
        toast.error('북마크 설정에 실패했습니다.');
      }
    }
  };

  // 알림 토글
  const toggleNotification = async (grantId: string) => {
    try {
      const result = await grantService.toggleNotification(grantId);
      setGrants(prevGrants => 
        prevGrants.map(g => 
          g.id === grantId ? { ...g, is_notified: result.is_notified } : g
        )
      );
    } catch (err: any) {
      console.error('알림 토글 실패:', err);
      if (err.message?.includes('401') || err.message?.includes('403')) {
        toast.warning('로그인이 필요한 기능입니다.');
      } else {
        toast.error('알림 설정에 실패했습니다.');
      }
    }
  };

  // 제안서 작성 페이지로 이동
  const handleCreateProposal = (grant: PublicGrant) => {
    navigate('/proposal-generator', { state: { selectedGrant: grant } });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case '모집중': return 'bg-green-100 text-green-800';
      case '마감임박': return 'bg-red-100 text-red-800';
      case '마감': return 'bg-gray-100 text-gray-800';
      case '예정': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSourceColor = (source: string) => {
    const colors: { [key: string]: string } = {
      'NTIS': 'bg-cyan-100 text-cyan-800 border-cyan-200',
      'IRIS': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'NIPA': 'bg-purple-100 text-purple-800 border-purple-200',
      'NIA': 'bg-blue-100 text-blue-800 border-blue-200',
      'KOCCA': 'bg-pink-100 text-pink-800 border-pink-200',
      'NRF': 'bg-teal-100 text-teal-800 border-teal-200',
      'BIZINFO': 'bg-green-100 text-green-800 border-green-200',
      'GBTP': 'bg-orange-100 text-orange-800 border-orange-200',
      'KEIT': 'bg-amber-100 text-amber-800 border-amber-200',
      'IITP': 'bg-violet-100 text-violet-800 border-violet-200',
      'KETEP': 'bg-lime-100 text-lime-800 border-lime-200',
      'KHIDI': 'bg-rose-100 text-rose-800 border-rose-200',
      'KSTARTUP': 'bg-sky-100 text-sky-800 border-sky-200',
      'SMTECH': 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
      'KONEPS': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    };
    return colors[source] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // CSV 내보내기
  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (selectedSource !== 'all') params.append('source', selectedSource);
    if (selectedStatus !== 'all') params.append('status', selectedStatus);
    if (selectedCategory !== 'all') params.append('category', selectedCategory);
    if (debouncedSearch) params.append('search', debouncedSearch);
    const [sortBy, sortOrder] = selectedSort.split('_');
    params.append('sort_by', sortBy);
    params.append('sort_order', sortOrder);

    const token = localStorage.getItem('proposalcraft_access_token');
    const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:8001'
      : `${window.location.protocol}//${window.location.hostname}:8001`;

    window.open(`${baseUrl}/api/public-grants/export/csv?${params.toString()}${token ? `&token=${token}` : ''}`, '_blank');
  };

  // 총 페이지 수
  const totalPages = Math.ceil(totalCount / pageSize);

  // 로딩 상태
  if (isLoading && grants.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        <span className="ml-2 text-gray-600">공모사업을 불러오는 중...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            공모사업 모니터링
          </h1>
          <p className="text-gray-600 mt-2">NTIS, IRIS, NIPA, NIA, 콘텐츠진흥원 등 실시간 공모사업 수집</p>
        </div>
        <div className="flex items-center space-x-3">
          {lastUpdated && (
            <span className="text-sm text-gray-500">
              마지막 업데이트: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button 
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-5 w-5 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            새로고침
          </button>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">전체 공모사업</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Radar className="h-8 w-8 text-blue-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">모집 중</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
            <FileText className="h-8 w-8 text-green-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">마감 임박 (2주 내)</p>
              <p className="text-2xl font-bold text-red-600">{stats.urgent}</p>
            </div>
            <Clock className="h-8 w-8 text-red-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">북마크</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.bookmarked}</p>
            </div>
            <Star className="h-8 w-8 text-yellow-400" />
          </div>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="공모사업명, 부처명 검색..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent w-full"
            />
          </div>
          
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            {sources.map((source) => (
              <option key={source.value} value={source.value}>{source.label}</option>
            ))}
          </select>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="all">전체 상태</option>
            <option value="모집중">모집중</option>
            <option value="마감임박">마감임박</option>
            <option value="예정">예정</option>
            <option value="마감">마감</option>
          </select>

          <select
            value={selectedSort}
            onChange={(e) => setSelectedSort(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            {sortOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <button
            onClick={handleExportCSV}
            title="CSV 내보내기"
            className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Download className="h-4 w-4 mr-1" />
            CSV
          </button>
        </div>
      </div>

      {/* 결과 요약 */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>총 {totalCount}건 중 {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalCount)}건</span>
      </div>

      {/* 공모사업 목록 */}
      <div className="space-y-4">
        {grants.map((grant) => (
          <div key={grant.id} className="card hover:shadow-medium transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getSourceColor(grant.source)}`}>
                    {grant.source_name || grant.source}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(grant.status)}`}>
                    {grant.status}
                  </span>
                  {grant.category && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                      {grant.category}
                    </span>
                  )}
                  {grant.match_score && (
                    <div className="flex items-center space-x-1 text-sm">
                      <Target className="h-4 w-4 text-emerald-500" />
                      <span className="font-medium text-emerald-600">매칭 {Math.round(grant.match_score)}%</span>
                    </div>
                  )}
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {grant.title}
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  {grant.ministry || grant.manage_agency || ''}
                </p>
                
                <div className="flex items-center space-x-6 text-sm text-gray-500 mb-3">
                  {grant.budget && (
                    <div className="flex items-center space-x-1">
                      <DollarSign className="h-4 w-4" />
                      <span>예산: {grant.budget}</span>
                    </div>
                  )}
                  {grant.deadline && (
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>마감: {grant.deadline}</span>
                    </div>
                  )}
                  {grant.expected_date && (
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>공고예정: {grant.expected_date}</span>
                    </div>
                  )}
                  {grant.d_day !== undefined && grant.d_day !== null && (
                    <div className={`flex items-center space-x-1 ${grant.d_day <= 14 ? 'text-red-600 font-medium' : ''}`}>
                      <Clock className="h-4 w-4" />
                      <span>D-{grant.d_day}</span>
                    </div>
                  )}
                </div>

                {/* 참여자격 */}
                {grant.eligibility && grant.eligibility.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="text-sm text-gray-600">참여자격:</span>
                    {grant.eligibility.map((elig, index) => (
                      <span key={index} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                        {elig}
                      </span>
                    ))}
                  </div>
                )}

                {/* 확장 설명 */}
                {expandedGrant === grant.id && grant.description && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">{grant.description}</p>
                  </div>
                )}
              </div>

              <div className="flex flex-col items-end space-y-3 ml-4">
                {/* 액션 버튼 */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => toggleBookmark(grant.id)}
                    title={grant.is_bookmarked ? "북마크 해제" : "북마크 추가"}
                    className={`p-2 rounded-lg transition-colors ${
                      grant.is_bookmarked 
                        ? 'bg-yellow-100 text-yellow-600' 
                        : 'bg-gray-100 text-gray-400 hover:text-yellow-600'
                    }`}
                  >
                    {grant.is_bookmarked ? <Star className="h-5 w-5 fill-current" /> : <StarOff className="h-5 w-5" />}
                  </button>
                  <button
                    onClick={() => toggleNotification(grant.id)}
                    title={grant.is_notified ? "알림 해제" : "알림 설정"}
                    className={`p-2 rounded-lg transition-colors ${
                      grant.is_notified 
                        ? 'bg-emerald-100 text-emerald-600' 
                        : 'bg-gray-100 text-gray-400 hover:text-emerald-600'
                    }`}
                  >
                    {grant.is_notified ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
                  </button>
                  {grant.url ? (
                    <a
                      href={grant.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="공고문 보기"
                      className="p-2 bg-gray-100 text-gray-400 hover:text-blue-600 rounded-lg transition-colors"
                    >
                      <ExternalLink className="h-5 w-5" />
                    </a>
                  ) : (
                    <button
                      disabled
                      title="공고문 링크 없음"
                      className="p-2 bg-gray-50 text-gray-300 rounded-lg cursor-not-allowed"
                    >
                      <ExternalLink className="h-5 w-5" />
                    </button>
                  )}
                </div>

                {/* 상세보기 / 제안서 작성 버튼 */}
                <div className="flex flex-col space-y-2">
                  <button
                    onClick={() => setExpandedGrant(expandedGrant === grant.id ? null : grant.id)}
                    className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    상세보기
                    <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${expandedGrant === grant.id ? 'rotate-180' : ''}`} />
                  </button>
                  <button 
                    onClick={() => handleCreateProposal(grant)}
                    className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    제안서 작성
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 빈 상태 */}
      {grants.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Radar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">공모사업을 찾을 수 없습니다</h3>
          <p className="text-gray-600">검색어나 필터를 변경하거나, 새로고침을 시도해보세요</p>
        </div>
      )}

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2 pt-4">
          <button
            onClick={() => setCurrentPage(0)}
            disabled={currentPage === 0}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
          >
            처음
          </button>
          <button
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
          >
            이전
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let page: number;
            if (totalPages <= 5) {
              page = i;
            } else if (currentPage < 3) {
              page = i;
            } else if (currentPage > totalPages - 4) {
              page = totalPages - 5 + i;
            } else {
              page = currentPage - 2 + i;
            }
            return (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1.5 text-sm border rounded-lg ${
                  currentPage === page
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {page + 1}
              </button>
            );
          })}

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
          >
            다음
          </button>
          <button
            onClick={() => setCurrentPage(totalPages - 1)}
            disabled={currentPage >= totalPages - 1}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
          >
            마지막
          </button>
        </div>
      )}
    </div>
  );
};

export default PublicGrantMonitoring;
